import type { LucideIcon } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import type {
  MenuItemChild,
  AppMenu,
  ProcessedMenuItem,
  ProcessedMenuGroup,
} from '@/utils/types/products';

function toKebabCase(name: string): string {
  return name
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replaceAll(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .replaceAll(/([a-zA-Z])(\d)/g, '$1-$2')
    .toLowerCase();
}

let shellReadyReported = false;

function reportShellReady(): void {
  if (shellReadyReported) return;
  if (performance.getEntriesByName('shell:script-start').length === 0) return;

  shellReadyReported = true;
  performance.mark('shell:menu-ready');
  const measure = performance.measure(
    'shell.ready',
    'shell:script-start',
    'shell:menu-ready'
  );

  void import('@/lib/datadog').then(({ trackPerformance }) =>
    trackPerformance('shell_ready', measure.duration, { category: 'shell' })
  );
}

class MenuCache {
  private processedMenu: ProcessedMenuGroup[] | null = null;
  private readonly routeToUrlMap = new Map<string, string>();
  private readonly iconCache = new Map<string, LucideIcon>();
  private isLoaded = false;

  async loadIcons(iconNames: Set<string>): Promise<void> {
    const toLoad = Array.from(iconNames).filter(
      name => !this.iconCache.has(name)
    );

    await Promise.all(
      toLoad.map(async name => {
        const importIcon = dynamicIconImports[toKebabCase(name) as keyof typeof dynamicIconImports];
        if (!importIcon) {
          console.warn(`[MenuUtils] Ícone Lucide não encontrado: ${name}`);
          return;
        }

        try {
          const mod = await importIcon();
          this.iconCache.set(name, mod.default as LucideIcon);
        } catch (error) {
          console.error(`[MenuUtils] Erro ao carregar ícone Lucide ${name}:`, error);
        }
      })
    );
  }

  getIcon(iconName: string): LucideIcon | null {
    if (this.iconCache.has(iconName)) {
      return this.iconCache.get(iconName)!;
    }

    return null;
  }

  getMicroFrontendUrl(route: string): string | null {
    return this.routeToUrlMap.get(route) || null;
  }

  getAllMicroFrontendEntries(): Array<{ route: string; url: string }> {
    return Array.from(this.routeToUrlMap.entries()).map(([route, url]) => ({
      route,
      url,
    }));
  }

  private extractPathFromUrl(urlMFE: string): string {
    try {
      const url = new URL(urlMFE);
      const pathname = url.pathname;
      return pathname.startsWith('/') ? pathname : `/${pathname}`;
    } catch {
      console.warn('[MenuUtils] URL inválida:', urlMFE);
      return '';
    }
  }

  private extractMicroFrontendPath(item: MenuItemChild): string {
    if (item.route) {
      const routeSegments = item.route
        .split('/')
        .filter(segment => segment.length > 0);
      if (routeSegments.length > 0) {
        return `/${routeSegments[0]}`;
      }
    }

    if (item.urlMFE) {
      return this.extractPathFromUrl(item.urlMFE);
    }

    return '';
  }

  private extractInternalPath(item: MenuItemChild): string {
    if (!item.route) return '';

    const routeSegments = item.route
      .split('/')
      .filter(segment => segment.length > 0);
    if (routeSegments.length > 1) {
      return `/${routeSegments.slice(1).join('/')}`;
    }

    return '';
  }

  async processMenuData(menuData: AppMenu[]): Promise<ProcessedMenuGroup[]> {
    this.clear();

    const iconNames = new Set<string>();
    menuData.forEach(menu => {
      this.extractUrlsFromItems(menu.menuItems);
      this.extractIconsFromItems(menu.menuItems, iconNames);
    });

    await this.loadIcons(iconNames);

    this.processedMenu = menuData
      .map(menu => ({
        id: menu.id,
        title: menu.label,
        product: menu.product,
        clientId: menu.clientId,
        isInternal: menu.isInternal,
        items: this.sortItems(menu.menuItems)
          .filter(item => {
            const isHome =
              item.route === '/nsapps/nsportal-home' ||
              item.urlMFE?.includes('/nsportal-home');
            return !isHome;
          })
          .map(item => this.processMenuItem(item, 0, menu.maxNestedLevels)),
      }))
      .filter(group => group.items.length > 0);

    this.isLoaded = true;
    reportShellReady();
    return this.processedMenu;
  }

  private extractUrlsFromItems(items: MenuItemChild[]): void {
    const processItem = (item: MenuItemChild) => {
      if (item.urlMFE) {
        const path = this.extractMicroFrontendPath(item);
        if (path) {
          this.routeToUrlMap.set(path, item.urlMFE);
        }
      }

      item.childMenuItems?.forEach(processItem);
    };

    items.forEach(processItem);
  }

  private extractIconsFromItems(
    items: MenuItemChild[],
    iconNames: Set<string>
  ): void {
    const processItem = (item: MenuItemChild) => {
      if (item.icon) iconNames.add(item.icon);
      item.childMenuItems?.forEach(processItem);
    };

    items.forEach(processItem);
  }

  private sortItems(items: MenuItemChild[]): MenuItemChild[] {
    return items.sort(
      (firstItem, secondItem) => firstItem.sorting - secondItem.sorting
    );
  }

  private processMenuItem(
    item: MenuItemChild,
    level: number = 0,
    maxLevels: number = 3,
    parentPath?: string
  ): ProcessedMenuItem {
    const isMicroFrontend =
      level === 0 && !!item.urlMFE && item.target === '_self';

    const processedItem: ProcessedMenuItem = {
      id: item.id,
      title: item.label,
      icon: item.icon,
      route: this.buildRoute(item, level, parentPath),
      url: item.urlMFE || item.route,
      target: item.target,
      isMicroFrontend,
    };

    if (item.childMenuItems?.length > 0 && level < maxLevels) {
      const pathForChildren =
        level === 0 && item.urlMFE
          ? this.extractMicroFrontendPath(item)
          : parentPath;

      processedItem.items = this.sortItems(item.childMenuItems).map(child =>
        this.processMenuItem(child, level + 1, maxLevels, pathForChildren)
      );
    }

    return processedItem;
  }

  private buildRoute(
    item: MenuItemChild,
    level: number,
    parentPath?: string
  ): string {
    if (level === 0 && item.urlMFE) {
      const path = this.extractMicroFrontendPath(item);
      const internalPath = this.extractInternalPath(item);
      return `/nsapps${path}${internalPath}`;
    }

    if (level > 0 && parentPath && item.route) {
      const childRoute = item.route.startsWith('/')
        ? item.route
        : `/${item.route}`;
      return `/nsapps${parentPath}${childRoute}`;
    }

    if (item.route) {
      const route = item.route.startsWith('/')
        ? item.route.slice(1)
        : item.route;
      return `/nsapps/${route}`;
    }

    return '';
  }

  getProcessedMenu(): ProcessedMenuGroup[] | null {
    return this.processedMenu;
  }

  isMenuLoaded(): boolean {
    return this.isLoaded;
  }

  async waitForMenu(): Promise<ProcessedMenuGroup[]> {
    if (this.isLoaded && this.processedMenu) {
      return this.processedMenu;
    }

    return new Promise((resolve, reject) => {
      const checkMenu = () => {
        if (this.isLoaded && this.processedMenu) {
          resolve(this.processedMenu);
        } else {
          setTimeout(checkMenu, 50);
        }
      };

      checkMenu();

      setTimeout(() => {
        reject(
          new Error('[MenuUtils] Timeout aguardando carregamento do menu')
        );
      }, 5000);
    });
  }

  clear(): void {
    this.processedMenu = null;
    this.routeToUrlMap.clear();
    this.iconCache.clear();
    this.isLoaded = false;
  }
}

const menuCache = new MenuCache();

export const getIcon = (iconName: string) => menuCache.getIcon(iconName);
export const getMicroFrontendUrl = (route: string) =>
  menuCache.getMicroFrontendUrl(route);
export const getAllMicroFrontendEntries = (): Array<{
  route: string;
  url: string;
}> => menuCache.getAllMicroFrontendEntries();
export const processMenuData = (menuData: AppMenu[]) =>
  menuCache.processMenuData(menuData);
export const waitForMenu = () => menuCache.waitForMenu();
export const clearMenuCache = () => menuCache.clear();
export const cleanRoute = (route: string) =>
  route.startsWith('/') ? route.slice(1) : route;
